import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/core/actions/dropdown-menu";
import { Button } from "@/components/core/buttons/button.tsx";
import { useModal } from "@/components/core/modals/use-modal.ts";
import { ScrollArea } from "@/components/core/scroller";
import { IconArchive, IconChevronDown, IconDots, IconPencilMinus, IconSearch, IconTerminal2, IconTrash, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";

export type ColumnDef<T> = {
	id: keyof T | string;
	label?: string | React.ReactNode;
	headerClassName?: string;
	cellClassName?: string;
	cell?: (props: {
		data: T;
		rowIndex: number;
		onClick?: (data: T, rowIndex: number) => void;
	}) => ReactNode;
	footClassName?: string;
	footLabel?: string;
	foot?: (props: { data: T; onClick?: (data: T) => void }) => ReactNode;
	renderCondition?: (data: T) => boolean;
};

export type DataTableProps<T> = {
	data?: T[];
	options?: (data: T) => ReactNode;
	columns: ColumnDef<T>[];
	className?: string;
	onEdit?: (data: T) => void;
	onRowClick?: (data: T) => void;
	onDelete?: (data: T) => void;
	onRemove?: (data: T) => void;
	onDump?: (data: T) => void;
	showBorder?: boolean;
	stickyHead?: boolean;
	stickyCol?: boolean;
	compact?: boolean;
	maxHeight?: number | string;
	showFooter?: React.ReactNode;
	customFooter?: React.ReactNode;
	showToolbar?: boolean;
	renderCondition?: (data: T) => boolean;
	actions?: React.ReactNode;
};

function TableSearch({
	value = "",
	onChange,
	onClear,
}: {
	value?: string;
	onChange: (value: string) => void;
	onClear?: () => void;
}) {
	return (
		<div className="flex flex-row items-center w-full gap-1">
			<div className="flex flex-row items-center w-full h-[38px] border border-border rounded-md overflow-hidden">
				<div className="inline-flex justify-center items-center h-[38px] w-[38px] shrink-0 text-foreground opacity-50">
					<IconSearch size={18} stroke={1.5} />
				</div>
				<input
					placeholder="Looking for something?"
					className="flex w-full h-full !border-0 !outline-none !focus:outline-none !focus:border-0 focus:ring-0 p-0 text-base text-foreground"
					value={value}
					autoComplete="off"
					onChange={(e) => onChange(e.target.value)}
				/>
				{value && (
					<button
						onClick={onClear}
						type="button"
						className="inline-flex justify-center items-center h-[38px] w-[38px] shrink-0 text-foreground opacity-50 hover:opacity-100"
					>
						<IconX size={18} stroke={1.5} />
					</button>
				)}
			</div>
		</div>
	);
}

export default function DataTable<T>({
	data,
	columns,
	options,
	className,
	onEdit,
	onRowClick,
	onDelete,
	onRemove, //for removing of data that has no id yet
	onDump,
	showBorder = false,
	stickyHead = false,
	stickyCol = false,
	compact = false,
	maxHeight,
	showFooter,
	customFooter,
	showToolbar = false,
	renderCondition = () => true,
	actions,
}: DataTableProps<T>) {
	const modal = useModal();

	/* actions */
	const handleCopyId = (row: T) => {
		console.log(row);
		toast.success("Dumped to console");
	};
	const handleArchive = (row: T) => {
		modal.openModal({
			type: "confirm",
			title: "Confirm Action",
			variant: "red",
			description: "Are you sure you want to archive this item?",
			onConfirm: async () => {
				onDelete?.(row);
			},
		});
	};

	const handleRemove = (row: T) => {
		modal.openModal({
			type: "confirm",
			title: "Confirm Action",
			variant: "red",
			description: "Are you sure you want to remove this item?",
			onConfirm: async () => {
				onRemove?.(row);
			},
		});
	};

	/* filter column */
	const [visibleColumns, setVisibleColumns] = useState<string[]>(
		showToolbar ? columns.slice(0, 4).map((col) => col.id as string) : columns.map((col) => col.id as string),
	);
	const toggleColumn = (columnId: string) => {
		setVisibleColumns((prev) => (prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId]));
	};

	/* table search */
	const [searchTerm, setSearchTerm] = useState("");

	const containsSearchTerm = (obj, searchTerm) => {
		const lowerSearch = searchTerm.toLowerCase();

		const search = (value) => {
			if (value == null) return false;

			if (typeof value === "object") {
				return Object.values(value).some(search);
			}

			return String(value).toLowerCase().includes(lowerSearch);
		};

		return search(obj);
	};

	const filteredData = data?.filter((item) => containsSearchTerm(item, searchTerm));

	return (
		<div className="flex flex-col w-full gap-4">
			{showToolbar && (
				<div className="flex flex-row w-full gap-2">
					<TableSearch value={searchTerm} onChange={setSearchTerm} onClear={() => setSearchTerm("")} />
					<DropdownMenu modal={false}>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost">
								Columns <IconChevronDown size={16} stroke={2} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							{columns.map((column) => (
								<DropdownMenuCheckboxItem
									key={column.id as string}
									checked={visibleColumns.includes(column.id as string)}
									onCheckedChange={() => toggleColumn(column.id as string)}
								>
									{column.label}
								</DropdownMenuCheckboxItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
					{actions && actions}
				</div>
			)}
			<ScrollArea
				className={clsx("flex flex-col w-full", showBorder && "rounded-md border border-border/50", className)}
				style={{ height: maxHeight ? `${maxHeight}px` : "auto" }}
			>
				<div className="flex flex-col w-full">
					<table className="w-full">
						<thead>
							<tr className="border-b-2 border-border/50">
								{columns
									.filter((col) => visibleColumns.includes(col.id as string))
									.map((column, columnIndex) => (
										<th
											key={columnIndex}
											className={clsx(
												"text-left text-foreground-dark",
												stickyHead &&
													"sticky top-0 bg-background before:absolute before:left-0 before:right-0 before:-bottom-[2px] before:h-[2px] before:w-full before:border-b-[2px] before:border-border/50",
												stickyCol &&
													"first:z-[2] first:sticky first:left-0 first:bg-background first:after:absolute first:after:right-0 first:after:top-0 first:after:bottom-0 first:after:w-[1px] first:after:h-full first:after:border-r first:after:border-border/50",
												compact ? "text-sm" : "text-base",
												!compact && !showBorder
													? "px-2 py-3"
													: compact
														? showBorder
															? "px-2 first:pl-4 last:pr-4 py-3"
															: "px-2 py-2"
														: "px-2 first:pl-4 last:pr-4 py-3",
												column.headerClassName,
											)}
										>
											{column.label}
										</th>
									))}
								{(onEdit || onDelete || onRemove || options) && (
									<th
										className={clsx(
											"text-left text-foreground-dark",
											stickyHead &&
												"sticky top-0 bg-background before:absolute before:left-0 before:right-0 before:-bottom-[2px] before:h-[2px] before:w-full before:border-b-[2px] before:border-border/50",
											stickyCol &&
												"first:z-[2] first:sticky first:left-0 first:bg-background first:after:absolute first:after:right-0 first:after:top-0 first:after:bottom-0 first:after:w-[1px] first:after:h-full first:after:border-r first:after:border-border/50",
											compact ? "text-sm" : "text-base",
											!compact && !showBorder
												? "px-2 py-3"
												: compact
													? showBorder
														? "px-2 first:pl-4 last:pr-4 py-3"
														: "px-2 py-2"
													: "px-2 first:pl-4 last:pr-4 py-3",
										)}
									/>
								)}
							</tr>
						</thead>
						<tbody>
							{filteredData?.map((r, rIndex) => (
								<tr key={rIndex} className="border-b border-border/50 last:border-b-0">
									{columns
										.filter((col) => visibleColumns.includes(col.id as string))
										.map((column, cIndex) => (
											<td
												key={cIndex}
												className={clsx(
													"text-left text-foreground",
													stickyCol &&
														"first:z-[1] first:sticky first:left-0 first:bg-background  first:after:absolute first:after:right-0 first:after:top-0 first:after:bottom-0 first:after:w-[1px] first:after:h-full first:after:border-r first:after:border-border/50",
													compact ? "text-sm" : "text-base",
													!compact && !showBorder
														? "px-2 py-3"
														: compact
															? showBorder
																? "px-2 first:pl-4 last:pr-4 py-3"
																: "px-2 py-2"
															: "px-2 first:pl-4 last:pr-4 py-3",
													column.cellClassName,
												)}
											>
												{column.cell?.({
													data: r,
													rowIndex: rIndex,
													onClick: onRowClick,
												})}
											</td>
										))}
									{(onEdit || onDelete || onRemove || options) && renderCondition(r) && (
										<td
											className={clsx(
												"text-right text-base foreground",
												compact ? "text-sm" : "text-base",
												!compact && !showBorder
													? "px-2 py-3"
													: compact
														? showBorder
															? "px-2 first:pl-4 last:pr-4 py-3"
															: "px-2 py-2"
														: "px-2 first:pl-4 last:pr-4 py-3",
											)}
										>
											<div className="flex flex-row justify-end">
												<DropdownMenu modal={false}>
													<DropdownMenuTrigger asChild>
														<Button className="!rounded-full !w-[24px] !h-[24px]" size="icon" variant="ghost">
															<IconDots size={16} stroke={1.5} />
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent>
														{onEdit && (
															<DropdownMenuItem onSelect={() => onEdit?.(r)}>
																<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																	<IconPencilMinus size={16} stroke={2} />
																	<span className="">Edit</span>
																</div>
															</DropdownMenuItem>
														)}

														{options?.(r)}

														{onDelete && (
															<>
																{onEdit && <DropdownMenuSeparator />}

																<DropdownMenuItem onSelect={() => handleArchive(r)} dangerZone>
																	<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																		<IconArchive size={16} stroke={2} />
																		<span className="">Archive</span>
																	</div>
																</DropdownMenuItem>
															</>
														)}
														{onRemove && (
															<>
																{onEdit && <DropdownMenuSeparator />}

																<DropdownMenuItem onSelect={() => handleRemove(r)} dangerZone>
																	<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																		<IconTrash size={16} stroke={2} />
																		<span className="">Remove</span>
																	</div>
																</DropdownMenuItem>
															</>
														)}

														{onDump && (
															<DropdownMenuItem onSelect={() => handleCopyId(r)} dangerZone>
																<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
																	<IconTerminal2 size={16} stroke={2} />
																	<span className="">Dump to console</span>
																</div>
															</DropdownMenuItem>
														)}
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</td>
									)}
								</tr>
							))}
						</tbody>
						{showFooter && (
							<tfoot>
								<tr className="border-t-2 border-border/50">
									{columns.map((column, columnIndex) => (
										<th
											key={columnIndex}
											className={clsx(
												"text-left text-base foreground",
												stickyCol &&
													"first:z-[2] first:sticky first:left-0 first:bg-background first:after:absolute first:after:right-0 first:after:top-0 first:after:bottom-0 first:after:w-[1px] first:after:h-full first:after:border-r first:after:border-border/50",
												compact ? "text-sm" : "text-base",
												compact ? "text-sm" : "text-base",
												!compact && !showBorder ? "px-2 py-3" : compact ? (showBorder ? "px-2 first:pl-4 py-2" : "px-2 py-2") : "px-2 first:pl-4 py-3",
												column.footClassName,
											)}
										>
											{column.footLabel}
										</th>
									))}
								</tr>
							</tfoot>
						)}
					</table>
					{customFooter && (
						<div
							className={clsx(
								"flex flex-row w-full border-t-2 border-border/50 text-base",
								compact ? "text-sm" : "text-base",
								!compact && !showBorder ? "px-2 py-3" : compact ? (showBorder ? "px-4 pr-14 py-2" : "px-2 py-2") : "px-2 py-3",
							)}
						>
							{customFooter}
						</div>
					)}
				</div>
			</ScrollArea>
		</div>
	);
}
